import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { controlClass, controlHeight } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/services/api";
import { popoverStyle, usePopoverPosition } from "@/hooks/use-popover-position";
import { queryClient } from "@/services/query-client";
import { cn } from "@/utils/utils";

export interface CompactOption {
  id: number;
  name: string;
}

const PAGE = 10;

/**
 * Every id and name this session has seen from a `/compact` endpoint.
 *
 * A response already carries the pair, so throwing it away and asking again is
 * a round trip for something we were told. Scrolling the Club dropdown once
 * names every club a chip, a column or a detail row can go on to show.
 *
 * It lives in the query cache rather than a module Map so that writing to it
 * re-renders whoever is reading — a label that arrives after paint is no use
 * if nothing repaints. Keyed by endpoint: ids are only unique within one.
 */
export const labelsKey = (endpoint: string) => ["compact-labels", endpoint] as const;

export function useCompactLabels(endpoint: string): Record<string, string> {
  return (
    useQuery({
      queryKey: labelsKey(endpoint),
      // Never fetches: the store is filled by `fetchCompact`, whoever calls it.
      enabled: false,
      initialData: {} as Record<string, string>,
      staleTime: Infinity,
    }).data ?? {}
  );
}

/** One page of a `/compact` endpoint: id and name, searched and offset on the server. */
export async function fetchCompact(
  endpoint: string,
  params: Record<string, string | number | number[] | undefined> = {},
  signal?: AbortSignal,
): Promise<CompactOption[]> {
  const options = (
    await api.get<CompactOption[]>(endpoint, { params, ...(signal ? { signal } : {}) })
  ).data;
  if (options.length > 0) {
    queryClient.setQueryData(labelsKey(endpoint), (seen: Record<string, string> = {}) => ({
      ...seen,
      ...Object.fromEntries(options.map((option) => [String(option.id), option.name])),
    }));
  }
  return options;
}

/**
 * A foreign-key picker: a search box over a `/compact` endpoint, loading ten
 * at a time as the list is scrolled. Native `<select>` cannot page, and a
 * barangay list has forty thousand rows.
 *
 * The chosen option's label is looked up by id when it is not on a loaded page,
 * so an edit form shows the current value without loading everything.
 *
 * Pass an array as `value` and it picks several: the menu stays open, each row
 * toggles, and the labels read as one line. The `/compact` routes take
 * `?id=1&id=2`, so the missing ones are still one request.
 */
export function FkSelect<V extends string | string[]>({
  id,
  endpoint,
  value,
  onChange,
  params,
  exclude,
  disabled = false,
  required = false,
  invalid = false,
  placeholder = "Select…",
  className,
}: {
  id?: string | undefined;
  endpoint: string;
  /** A string picks one; an array picks several, and is kept sorted. */
  value: V;
  onChange: (value: V, label: string) => void;
  /** Extra query for the endpoint — a parent id for a dependent list. */
  params?: Record<string, string | number | undefined> | undefined;
  /** Options to leave out, such as the record itself. */
  exclude?: ((option: CompactOption) => boolean) | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  invalid?: boolean | undefined;
  placeholder?: string | undefined;
  className?: string | undefined;
}) {
  const listId = useId();
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLLIElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const box = usePopoverPosition(root, open);

  // Debounce what is typed; the server sees a search every 250ms at most.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const pages = useInfiniteQuery({
    queryKey: ["compact", endpoint, params ?? {}, term],
    queryFn: ({ pageParam, signal }) =>
      fetchCompact(
        endpoint,
        { ...params, search: term || undefined, limit: PAGE, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length === PAGE ? all.length * PAGE : undefined),
    enabled: open,
    staleTime: 60_000,
  });
  const options = (pages.data?.pages.flat() ?? []).filter((option) => !exclude?.(option));

  const many = Array.isArray(value);
  const picked = many ? value : value === "" ? [] : [value as string];

  // The labels for values nothing loaded has shown yet — one request for all
  // of them, because /compact takes the key repeated.
  const known = new Map(options.map((option) => [String(option.id), option.name]));
  const missing = picked.filter((one) => !known.has(one));
  const current = useQuery({
    queryKey: ["compact", endpoint, "id", missing.join(",")],
    queryFn: async ({ signal }) => fetchCompact(endpoint, { id: missing.map(Number) }, signal),
    enabled: missing.length > 0,
    staleTime: Infinity,
  });
  for (const option of current.data ?? []) known.set(String(option.id), option.name);
  const label = picked.map((one) => known.get(one) ?? `#${one}`).join(", ");

  // Close on a press outside or Escape.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      // The list hangs outside the root now, so both count as "inside".
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  // Load the next page when the end of the list scrolls into view.
  useEffect(() => {
    const target = sentinel.current;
    if (!open || !target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && pages.hasNextPage && !pages.isFetchingNextPage) {
          void pages.fetchNextPage();
        }
      },
      { root: list.current },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [open, pages]);

  const pick = (option: CompactOption) => {
    const one = String(option.id);
    if (!many) {
      onChange(one as unknown as V, option.name);
      setOpen(false);
      setSearch("");
      return;
    }
    // Sorted, so the same set of clubs is the same value however it was
    // clicked — which is what lets a saved set match and a query key settle.
    const kept = picked.includes(one) ? picked.filter((x) => x !== one) : [...picked, one];
    const next = [...kept].sort();
    onChange(next as unknown as V, next.map((x) => known.get(x) ?? `#${x}`).join(", "));
  };
  const clear = () => onChange((many ? [] : "") as unknown as V, "");

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          controlClass,
          controlHeight.md,
          "flex items-center gap-2 text-left",
          open && "border-accent ring-[3px] ring-[var(--accent-ring)]",
        )}
      >
        <span className={cn("min-w-0 flex-1 truncate", !label && "text-fg-subtle")}>
          {picked.length === 0 ? placeholder : current.isPending && !label ? "…" : label}
        </span>
        {picked.length > 0 && !disabled ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear"
            onClick={(event) => {
              event.stopPropagation();
              clear();
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                event.stopPropagation();
                clear();
              }
            }}
            className="text-fg-subtle hover:bg-surface-inset hover:text-fg grid size-5 shrink-0 place-items-center rounded-full transition-colors"
          >
            <X className="size-4" aria-hidden />
          </span>
        ) : null}
        <ChevronDown className="text-fg-subtle size-4 shrink-0" aria-hidden />
      </button>
      {/* Native validity for the form: empty and required blocks submit. */}
      <input
        tabIndex={-1}
        aria-hidden
        required={required}
        value={picked.join(",")}
        onChange={() => undefined}
        className="pointer-events-none absolute inset-x-3 bottom-0 h-px opacity-0"
      />

      {open ? (
        <div
          ref={menu}
          style={popoverStyle(box)}
          className="border-line bg-surface-raised animate-sheet fixed z-50 origin-top overflow-hidden rounded-xl border shadow-[var(--shadow-float)]"
        >
          <label className="hairline-b relative block">
            <Search
              className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              aria-hidden
            />
            <input
              autoFocus
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search…"
              className="text-fg text-callout placeholder:text-fg-subtle h-10 w-full bg-transparent pr-3 pl-9 outline-none"
            />
          </label>
          <ul ref={list} id={listId} role="listbox" className="max-h-64 overflow-y-auto p-1">
            {options.map((option) => {
              const selected = picked.includes(String(option.id));
              return (
                <li key={option.id} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => pick(option)}
                    className={cn(
                      "text-callout hover:bg-surface-inset flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors",
                      selected ? "text-accent font-medium" : "text-fg",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{option.name}</span>
                    {selected ? <Check className="size-4 shrink-0" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
            {pages.isPending || pages.isFetchingNextPage ? (
              <li className="text-fg-muted text-callout flex items-center gap-2 px-2.5 py-2">
                <Spinner /> Loading…
              </li>
            ) : options.length === 0 ? (
              <li className="text-fg-subtle text-callout px-2.5 py-2">No matches</li>
            ) : null}
            <li ref={sentinel} aria-hidden className="h-px" />
          </ul>
        </div>
      ) : null}
    </div>
  );
}
